import React, { useState, useRef, useEffect } from 'react';

const ListingCard = ({ 
  listing, 
  isSelected, 
  onSelect, 
  onViewDetails, 
  onAction,
  onRefresh,
  imageBaseUrl 
}) => {
  const [imageError, setImageError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const imageRef = useRef(null);
  
  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        rootMargin: '50px',
        threshold: 0.1
      }
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => {
      if (imageRef.current) {
        observer.unobserve(imageRef.current);
      }
    };
  }, []);
  
  const getListingImage = () => {
    if (!listing || !isVisible) return null;
    
    // Priority 1: Use first_image_url if available
    if (listing.first_image_url && !imageError) {
      return `${imageBaseUrl}${listing.first_image_url}`;
    }
    
    // Priority 2: Use images array if available
    if (listing.images && listing.images.length > 0 && !imageError) {
      let firstImage = listing.images[0];
      if (typeof firstImage === "object") {
        firstImage = firstImage.image_url || firstImage.url || "";
      }
      if (firstImage) {
        return `${imageBaseUrl}${firstImage}`;
      }
    }
    
    // Priority 3: Check if image_url is directly on listing
    if (listing.image_url && !imageError) {
      return `${imageBaseUrl}${listing.image_url}`;
    }
    
    return null;
  };
  
  const getCategoryIcon = () => {
    if (!listing.category_name) return '📦';
    
    const category = listing.category_name.toLowerCase();
    if (category.includes('phone') || category.includes('electronic')) return '📱';
    if (category.includes('car') || category.includes('vehicle')) return '🚗';
    if (category.includes('property') || category.includes('house')) return '🏠';
    if (category.includes('service')) return '🔧';
    if (category.includes('fashion') || category.includes('cloth')) return '👕';
    if (category.includes('food')) return '🍕';
    if (category.includes('book')) return '📚';
    if (category.includes('sport')) return '⚽';
    
    return '📦';
  };
  
  const getSpecializedBadge = () => {
    const type = listing.specialized_type || (listing.specialized_data?._type);
    
    const badges = {
      'electronics': { label: 'Electronics', color: 'bg-blue-900/30 text-blue-400 border-blue-800/50' },
      'vehicles': { label: 'Vehicle', color: 'bg-[#ff660e]/20 text-[#ff660e] border-[#ff660e]/30' },
      'properties': { label: 'Property', color: 'bg-green-900/30 text-green-400 border-green-800/50' },
      'services': { label: 'Service', color: 'bg-purple-900/30 text-purple-400 border-purple-800/50' },
      'general': { label: 'General', color: 'bg-gray-700 text-gray-300 border-gray-600' }
    };
    
    const badge = badges[type];
    if (badge) {
      return (
        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${badge.color}`}>
          {badge.label}
        </span>
      );
    }
    return null;
  };
  
  const getStatusBadge = () => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full border";
    
    if (listing.status === 'sold') {
      return <span className={`${baseClasses} bg-gray-700 text-gray-300 border-gray-600`}>Sold</span>;
    }
    if (listing.status === 'inactive') {
      return <span className={`${baseClasses} bg-gray-700 text-gray-300 border-gray-600`}>Inactive</span>;
    }

    if (listing.moderation_status === 'pending') {
      return <span className={`${baseClasses} bg-yellow-900/30 text-yellow-400 border-yellow-800/50`}>Pending Review</span>;
    }
    if (listing.moderation_status === 'rejected') {
      return <span className={`${baseClasses} bg-red-900/30 text-red-400 border-red-800/50`}>Rejected</span>;
    }
    
    return <span className={`${baseClasses} bg-green-900/30 text-green-400 border-green-800/50`}>Active</span>;
  };
  
  const getFeaturedBadge = () => {
    if (listing.is_featured) {
      return (
        <span className="px-2 py-1 bg-purple-900/30 text-purple-400 text-xs font-medium rounded-full border border-purple-800/50">
          ⭐ Featured
        </span>
      );
    }
    return null;
  };
  
  const getBoostBadge = () => {
    if (listing.has_active_boost || listing.is_boosted) {
      return (
        <span className="px-2 py-1 bg-[#ff660e]/20 text-[#ff660e] text-xs font-medium rounded-full border border-[#ff660e]/30">
          🚀 Boosted
        </span>
      );
    }
    return null;
  };
  
  const getAdminStatsBadge = () => {
    if (listing.admin_stats?.report_count > 0) {
      return (
        <span className="px-2 py-1 bg-red-900/30 text-red-400 text-xs font-medium rounded-full border border-red-800/50">
          ⚠️ {listing.admin_stats.report_count} Report(s)
        </span>
      );
    }
    return null;
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount || 0);
  };
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const imageUrl = getListingImage();
  const categoryIcon = getCategoryIcon();
  
  return (
    <div 
      ref={imageRef}
      className={`bg-gray-800 rounded-xl shadow-lg border-2 transition-all duration-300 hover:shadow-xl hover:scale-[1.02] overflow-hidden group ${
        isSelected ? 'border-[#ff660e] ring-2 ring-[#ff660e]/30' : 'border-gray-700'
      }`}
    >
      {/* Selection Checkbox */}
      <div className="absolute top-4 left-4 z-10">
        <button
          onClick={onSelect}
          className={`w-6 h-6 rounded-md flex items-center justify-center transition-all duration-200 hover:scale-110 ${
            isSelected 
              ? 'bg-[#ff660e] border-[#ff660e] shadow-lg' 
              : 'bg-gray-700 border border-gray-600 hover:border-[#ff660e]'
          }`}
        >
          {isSelected && (
            <span className="text-white text-sm">✓</span>
          )}
        </button>
      </div>
      
      {/* Listing Image with Lazy Loading */}
      <div className="h-48 relative bg-gradient-to-br from-gray-900 to-gray-800 overflow-hidden">
        {isVisible ? (
          <>
            {imageUrl ? (
              <img 
                src={imageUrl}
                alt={listing.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                onError={() => setImageError(true)}
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="text-5xl mb-3 text-gray-500 group-hover:text-[#ff660e] transition-colors">{categoryIcon}</div>
                  <p className="text-sm font-medium text-gray-400">
                    {listing.image_count > 0 ? `${listing.image_count} images` : 'No images'}
                  </p>
                </div>
              </div>
            )}
            
            {/* Loading Placeholder */}
            {!imageUrl && isVisible && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#ff660e] border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </>
        ) : (
          // Loading skeleton for images not yet visible
          <div className="w-full h-full bg-gradient-to-br from-gray-900 to-gray-800 animate-pulse flex items-center justify-center">
            <div className="text-5xl text-gray-700">{categoryIcon}</div>
          </div>
        )}
        
        {/* Badges */}
        <div className="absolute top-3 right-3 left-3 flex flex-wrap gap-1 justify-end">
          <div className="flex flex-wrap gap-1">
            {getSpecializedBadge()}
            {getFeaturedBadge()}
            {getBoostBadge()}
            {getAdminStatsBadge()}
          </div>
          {getStatusBadge()}
        </div>
      </div>
      
      {/* Listing Info */}
      <div className="p-4">
        <div className="space-y-3">
          {/* Title and Price */}
          <div>
            <h3 className="text-lg font-bold text-white mb-1 line-clamp-2 leading-tight group-hover:text-[#ff660e] transition-colors cursor-pointer">
              {listing.title}
            </h3>
            <p className="text-2xl font-bold text-[#ff660e] group-hover:text-orange-400 transition-colors">
              {formatCurrency(listing.price)}
            </p>
            {listing.is_negotiable && (
              <p className="text-xs text-gray-400 mt-1 flex items-center gap-1 group-hover:text-gray-300 transition-colors">
                <span>💬</span>
                <span>Price negotiable</span>
              </p>
            )}
          </div>

          {/* Category and Stock */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-gray-400 group-hover:text-gray-300 transition-colors">
              <span>{categoryIcon}</span>
              <span className="font-medium">{listing.category_name}</span>
            </div>
            <span className="text-gray-400 font-medium group-hover:text-gray-300 transition-colors">
              Stock: {listing.quantity_available}
            </span>
          </div>

          {/* Seller Info */}
          <div className="border-t border-gray-700 pt-2 group-hover:border-gray-600 transition-colors">
            <p className="text-sm font-medium text-gray-300 mb-1 group-hover:text-white transition-colors">Seller</p>
            <div className="flex items-center justify-between">
              <p className="text-sm text-white font-semibold truncate group-hover:text-[#ff660e] transition-colors">
                {listing.store_name}
              </p>
              <p className="text-xs text-gray-500 truncate ml-2 group-hover:text-gray-400 transition-colors">
                {listing.seller_email}
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center justify-between text-sm text-gray-500 border-t border-gray-700 pt-2 group-hover:border-gray-600 group-hover:text-gray-400 transition-colors">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1 group-hover:text-[#ff660e] transition-colors">
                <span>👁️</span>
                <span className="font-medium">{listing.view_count || 0}</span>
              </span>
              <span className="flex items-center gap-1 group-hover:text-[#ff660e] transition-colors">
                <span>🛒</span>
                <span className="font-medium">{listing.order_count || 0}</span>
              </span>
              <span className="flex items-center gap-1 group-hover:text-[#ff660e] transition-colors">
                <span>📸</span>
                <span className="font-medium">{listing.image_count || 0}</span>
              </span>
            </div>
            <span className="text-xs font-medium text-gray-500 group-hover:text-gray-400 transition-colors">
              {formatDate(listing.created_at)}
            </span>
          </div>
        </div>
      </div>
      
      {/* Actions */}
      <div className="px-4 py-3 bg-gray-900/50 border-t border-gray-700 group-hover:bg-gray-900/70 transition-colors">
        <div className="flex items-center justify-between">
          <button
            onClick={onViewDetails}
            className="text-sm font-medium transition-all duration-200 flex items-center gap-1 group-hover:scale-[1.02]"
            style={{ color: '#ff660e' }}
          >
            <span className="group-hover:rotate-12 transition-transform">🔍</span>
            <span className="group-hover:text-orange-400 transition-colors">View Details</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ListingCard;